import { useMemo, useState, useEffect, useRef, useCallback } from 'react'
import { pdf } from '@react-pdf/renderer'
import * as pdfjsLib from 'pdfjs-dist'
import { PDFSkeleton } from '../ui/Skeleton'
import { CleanTemplate } from '../cv-templates/CleanTemplate'
import { BoldTemplate } from '../cv-templates/BoldTemplate'
import { CreativeTemplate } from '../cv-templates/CreativeTemplate'
import { TechTemplate } from '../cv-templates/TechTemplate'
import { ExecutiveTemplate } from '../cv-templates/ExecutiveTemplate'
import { ModernTemplate } from '../cv-templates/ModernTemplate'
import { useTranslation } from '../../contexts/LanguageContext'
import type { CV } from '../../types/cv'
import type { TemplateType } from '../../hooks/usePdfGenerator'

// Configure pdf.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs'

interface CVPreviewProps {
  cv: CV
  template?: TemplateType
  colorPalette?: string
}

/**
 * CVPreview renders the actual PDF template to ensure
 * what the user sees is EXACTLY what they will download.
 *
 * Uses pdf.js to render PDF pages directly onto canvas elements
 * for pixel-perfect, resolution-independent rendering.
 */
export function CVPreview({ cv, template = 'clean', colorPalette }: CVPreviewProps) {
  const { t } = useTranslation()
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null)
  const [, setPageCount] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [, setIsRendering] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const canvasContainerRef = useRef<HTMLDivElement>(null)

  // Check if CV is empty
  const isEmpty = useMemo(() => {
    const hasPersonalInfo =
      cv.personalInfo?.fullName ||
      cv.personalInfo?.title ||
      cv.personalInfo?.email ||
      cv.personalInfo?.phone ||
      cv.personalInfo?.location ||
      (cv.personalInfo?.links && cv.personalInfo.links.length > 0)

    const hasExperience = cv.experience && cv.experience.length > 0
    const hasEducation = cv.education && cv.education.length > 0
    const hasSkills =
      (cv.skills?.technical && cv.skills.technical.length > 0) ||
      (cv.skills?.soft && cv.skills.soft.length > 0) ||
      (cv.skills?.tools && cv.skills.tools.length > 0)
    const hasLanguages = cv.languages && cv.languages.length > 0

    return (
      !hasPersonalInfo &&
      !cv.summary &&
      !hasExperience &&
      !hasEducation &&
      !hasSkills &&
      !hasLanguages
    )
  }, [cv])

  // Generate the PDF document based on template
  const pdfDocument = useMemo(() => {
    if (isEmpty) return null

    switch (template) {
      case 'bold':
        return BoldTemplate({ cv, colorPalette })
      case 'creative':
        return CreativeTemplate({ cv, colorPalette })
      case 'tech':
        return TechTemplate({ cv, colorPalette })
      case 'executive':
        return ExecutiveTemplate({ cv, colorPalette })
      case 'modern':
        return ModernTemplate({ cv, colorPalette })
      case 'clean':
      default:
        return CleanTemplate({ cv, colorPalette })
    }
  }, [cv, template, colorPalette, isEmpty])

  // Render PDF pages directly to canvas elements in the DOM
  const renderPdfToCanvases = useCallback(async (blob: Blob) => {
    const container = canvasContainerRef.current
    if (!container) return

    const arrayBuffer = await blob.arrayBuffer()
    const pdfDoc = await pdfjsLib.getDocument({ data: arrayBuffer }).promise
    const numPages = pdfDoc.numPages

    const dpr = Math.max(window.devicePixelRatio || 3, 3)
    // Render at high resolution (A4 width * 3x minimum), display with CSS width:100%
    const renderWidth = 595 * dpr // A4 width in points

    // Clear previous canvases
    container.innerHTML = ''

    for (let i = 1; i <= numPages; i++) {
      const page = await pdfDoc.getPage(i)
      const viewport = page.getViewport({ scale: 1 })

      const renderScale = renderWidth / viewport.width
      const renderViewport = page.getViewport({ scale: renderScale })

      const canvas = document.createElement('canvas')
      canvas.width = renderViewport.width
      canvas.height = renderViewport.height

      // Let CSS handle sizing — canvas stretches to fill container width
      canvas.style.width = '100%'
      canvas.style.height = 'auto'
      canvas.style.display = 'block'
      canvas.style.borderRadius = '0.5rem'
      canvas.className = 'shadow-lg'

      const context = canvas.getContext('2d')!

      await page.render({
        canvasContext: context,
        viewport: renderViewport,
        canvas,
      }).promise

      container.appendChild(canvas)
    }

    setPageCount(numPages)
  }, [])

  // Generate PDF blob
  useEffect(() => {
    if (!pdfDocument) {
      setPdfBlob(null)
      setPageCount(0)
      setIsLoading(false)
      return
    }

    let isMounted = true
    setIsLoading(true)
    setError(null)

    // Debounce PDF generation to avoid too many renders while typing
    const timeoutId = setTimeout(async () => {
      try {
        const blob = await pdf(pdfDocument).toBlob()
        if (isMounted) {
          setPdfBlob(blob)
        }
      } catch (err) {
        if (isMounted) {
          console.error('Error generating PDF preview:', err)
          setError('Error al generar la vista previa')
          setIsLoading(false)
        }
      }
    }, 300) // 300ms debounce

    return () => {
      isMounted = false
      clearTimeout(timeoutId)
    }
  }, [pdfDocument])

  // Render PDF blob to canvas elements
  useEffect(() => {
    if (!pdfBlob) {
      setPageCount(0)
      return
    }

    let isMounted = true
    setIsRendering(true)

    renderPdfToCanvases(pdfBlob)
      .then(() => {
        if (isMounted) {
          setIsLoading(false)
          setIsRendering(false)
        }
      })
      .catch((err) => {
        console.error('Error rendering PDF to canvas:', err)
        if (isMounted) {
          setError('Error al generar la vista previa')
          setIsLoading(false)
          setIsRendering(false)
        }
      })

    return () => {
      isMounted = false
    }
  }, [pdfBlob, renderPdfToCanvases])

  // Canvas container is always mounted so refs are available for rendering.
  // Overlay states (empty, loading, error) are shown on top.
  return (
    <div ref={containerRef} className="h-full w-full relative overflow-y-auto">
      {/* Canvas container - always in DOM */}
      <div className="flex flex-col items-center p-2 sm:p-4">
        <div
          ref={canvasContainerRef}
          className="flex flex-col gap-3 w-full max-w-[21cm]"
        />
      </div>

      {/* Empty state */}
      {isEmpty && (
        <div className="absolute inset-0 flex items-center justify-center text-slate-400 bg-slate-100">
          <div className="text-center">
            <svg
              className="w-16 h-16 mx-auto mb-4 text-slate-300"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <p className="text-lg font-medium">{t.emptyState.title}</p>
            <p className="text-sm mt-1">{t.emptyState.subtitle}</p>
          </div>
        </div>
      )}

      {/* Loading state */}
      {isLoading && !isEmpty && (
        <div className="absolute inset-0 flex items-start justify-center p-2 sm:p-4 bg-slate-100">
          <PDFSkeleton />
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center text-red-500 bg-slate-100">
          <div className="text-center">
            <svg
              className="w-12 h-12 mx-auto mb-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <p className="text-sm">{error}</p>
          </div>
        </div>
      )}
    </div>
  )
}
